// One wall seen from inside the room: openings, obstructions, utility points and zones with
// their partitions, to scale. Offsets run from the wall's start corner (left).

import { cn } from "@artisan/ui";

import { PURPOSES, type RoomDraft, wallLabel } from "../project/model";
import { umToMmText } from "../survey/units";

export function ElevationView({
  room,
  wall,
  className,
  selected,
  onSelect,
}: {
  room: RoomDraft;
  wall: number;
  className?: string;
  selected?: string | null;
  onSelect?: (id: string) => void;
}) {
  const wallM = room.walls[wall] ?? null;
  const L = (wallM?.um ?? 3_000_000) / 1000;
  const H = (room.ceiling?.um ?? 2_700_000) / 1000;
  const k = Math.max(L, H) / 40;
  const pad = k * 3;
  const y = (z: number) => H - z; // svg y from floor height
  const item = (id: string) => ({
    onClick: onSelect ? () => onSelect(id) : undefined,
    className: onSelect ? "cursor-pointer" : undefined,
  });
  const stroke = (id: string) => (selected === id ? "var(--primary)" : undefined);
  return (
    <svg viewBox={`${-pad} ${-pad} ${L + 2 * pad} ${H + 2 * pad}`} className={cn("block", className)} role="img" aria-label={`${wallLabel(wall)} хананы өрөг`}>
      <rect x={0} y={0} width={L} height={H} fill="var(--card)" stroke="var(--foreground)" strokeWidth={k * 0.15} />
      {room.zones
        .filter((z) => z.wall === wall)
        .map((z) => (
          <g key={z.id} {...item(z.id)}>
            <rect
              x={z.offset / 1000}
              y={y((z.elevation + z.height) / 1000)}
              width={z.length / 1000}
              height={z.height / 1000}
              fill={z.kind === "wall" ? "color-mix(in oklab, var(--oak) 25%, var(--card))" : "color-mix(in oklab, var(--oak) 45%, var(--card))"}
              stroke={stroke(z.id) ?? "var(--oak)"}
              strokeWidth={k * (selected === z.id ? 0.3 : 0.12)}
            />
            {z.partitions.map((p) => {
              const x = (z.offset + p.offset) / 1000;
              const w = p.width / 1000;
              const top = y((z.elevation + z.height) / 1000);
              return (
                <g key={p.id}>
                  <line x1={x} x2={x} y1={top} y2={top + z.height / 1000} stroke="var(--oak)" strokeWidth={k * 0.1} />
                  {w > k * 4 ? (
                    <text x={x + w / 2} y={top + z.height / 2000} textAnchor="middle" fontSize={k * 0.85} fill="var(--foreground)">
                      <tspan x={x + w / 2}>{p.label || PURPOSES[p.purpose]}</tspan>
                      <tspan x={x + w / 2} dy={k * 1.1} fontFamily="var(--font-mono)" fill="var(--muted-foreground)">
                        {umToMmText(p.width)}
                      </tspan>
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        ))}
      {room.openings.map((o) => {
        if (o.wall !== wall || !o.offset || !o.width || !o.height) return null;
        const sill = o.sill?.um ?? 0;
        const [x0, w, h] = [o.offset.um, o.width.um, o.height.um];
        return (
          <g key={o.id} {...item(o.id)}>
            <rect
              x={x0 / 1000}
              y={y((sill + h) / 1000)}
              width={w / 1000}
              height={h / 1000}
              fill={o.kind === "window" ? "color-mix(in oklab, var(--chart-2) 18%, var(--card))" : "var(--muted)"}
              stroke={stroke(o.id) ?? (o.kind === "window" ? "var(--chart-2)" : "var(--muted-foreground)")}
              strokeWidth={k * (selected === o.id ? 0.3 : 0.15)}
            />
            <text x={(x0 + w / 2) / 1000} y={y((sill + h / 2) / 1000)} textAnchor="middle" fontSize={k * 0.9} fill="var(--muted-foreground)">
              {o.kind === "window" ? "Цонх" : o.kind === "door" ? "Хаалга" : "Нүх"}
            </text>
          </g>
        );
      })}
      {room.obstructions.map((o) => {
        if (o.wall !== wall || !o.offset || !o.width || !o.height || !o.elevation) return null;
        return (
          <rect
            key={o.id}
            {...item(o.id)}
            x={o.offset.um / 1000}
            y={y((o.elevation.um + o.height.um) / 1000)}
            width={o.width.um / 1000}
            height={o.height.um / 1000}
            fill="color-mix(in oklab, var(--muted-foreground) 22%, var(--card))"
            stroke={stroke(o.id) ?? "var(--muted-foreground)"}
            strokeWidth={k * (selected === o.id ? 0.3 : 0.12)}
          />
        );
      })}
      {room.utilities.map((u) => {
        if (u.wall !== wall || !u.offset || !u.height) return null;
        return (
          <circle
            key={u.id}
            {...item(u.id)}
            cx={u.offset.um / 1000}
            cy={y(u.height.um / 1000)}
            r={k * 0.6}
            fill={u.kind.startsWith("water") ? "var(--chart-1)" : u.kind === "gas" ? "var(--warning)" : u.kind === "drain" ? "var(--chart-5)" : "var(--chart-2)"}
            stroke={stroke(u.id) ?? "var(--card)"}
            strokeWidth={k * 0.15}
          />
        );
      })}
      {/* dimensions */}
      <line x1={0} x2={L} y1={H + k * 1.4} y2={H + k * 1.4} stroke="var(--muted-foreground)" strokeWidth={k * 0.08} />
      <text x={L / 2} y={H + k * 2.6} textAnchor="middle" fontSize={k} fontFamily="var(--font-mono)" fill="var(--foreground)">
        {wallLabel(wall)} · {wallM ? umToMmText(wallM.um) : "?"}
      </text>
      <text x={-k * 1.2} y={H / 2} textAnchor="middle" fontSize={k} fontFamily="var(--font-mono)" fill="var(--muted-foreground)" transform={`rotate(-90 ${-k * 1.2} ${H / 2})`}>
        {room.ceiling ? umToMmText(room.ceiling.um) : "тааз ?"}
      </text>
    </svg>
  );
}
